import React, { SetStateAction } from "react";
import { FormControl, FormControlLabel, FormLabel, RadioGroup } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RenderHTML } from "components";

import {
  ErrorMessage,
  RadioField,
  StyledRadio,
  TextAreaComponent,
} from "../../shared/styled-components";
import { useMCQWithCommentQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface MCQOption {
  optionId: string;
  htmlText: string;
  sequence: number;
  isSelected: boolean | null;
}

interface MCQWithCommentQuestionProps {
  questionId: string;
  questionText: string;
  options: MCQOption[];
  isMandatory: boolean;
  questionIndex: number;
  commentValue: string;
  showMandatoryError: boolean;
  onChange: (questionId: string, optionId: string) => void;
  onCommentChange: (comment: string) => void;
}

export const MCQWithCommentQuestion: React.FC<MCQWithCommentQuestionProps> = ({
  questionId,
  questionText,
  options,
  isMandatory,
  questionIndex,
  commentValue,
  showMandatoryError,
  onChange,
  onCommentChange,
}) => {
  const { t } = useTranslation();
  const classes = useMCQWithCommentQuestionStyles();
  const [optionId, setOptionId] = React.useState<string | null>(null);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptionId(event.target.value);
    onChange(questionId, event.target.value);
  };

  const handleCommentChange = (event: { target: { value: SetStateAction<string> } }) => {
    onCommentChange(event.target.value as string);
  };

  const radioGroupId = `mcq-comment-${questionId}`;
  const textareaId = `${radioGroupId}-textarea`;
  const errorId = `${radioGroupId}-error`;

  return (
    <>
      <RadioField>
        <FormControl component="fieldset" aria-disabled="false" fullWidth>
          <FormLabel
            component="legend"
            className={classes.formLabel}
            aria-label={
              isMandatory ? `${questionText} ${ErrorMessages.InputRequired}` : questionText
            }
          >
            <div className={classes.questionText}>
              <RenderHTML content={questionText ?? ""} />
              {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
            </div>
          </FormLabel>
          <div className={showMandatoryError && isMandatory ? classes.containerError : ""}>
            <RadioGroup
              id={radioGroupId}
              name={`${t("question").toLowerCase()} ${questionIndex + 1}`}
              onChange={handleRadioChange}
              row={false}
              aria-describedby={showMandatoryError && isMandatory ? errorId : undefined}
            >
              {options?.map((option) => (
                <FormControlLabel
                  key={option?.optionId}
                  value={option?.optionId}
                  aria-label={` ${option?.htmlText ?? ""}`}
                  control={<StyledRadio />}
                  label={
                    <div className={classes.optionLabel}>
                      <RenderHTML content={option?.htmlText ?? ""} />
                    </div>
                  }
                />
              ))}
            </RadioGroup>

            <div className={classes.commentSection}>
              <FormLabel className={classes.commentLabel} htmlFor={textareaId}>
                {t("please_provide_comment")}
              </FormLabel>
            </div>

            <TextAreaComponent
              id={textareaId}
              value={commentValue}
              name={`${t("question").toLowerCase()} ${questionIndex + 1} comment`}
              onChange={handleCommentChange}
              placeholder=""
              rows={5}
              aria-describedby={showMandatoryError && isMandatory ? errorId : undefined}
            />
          </div>
        </FormControl>
      </RadioField>

      {(isMandatory && !optionId) || (showMandatoryError && isMandatory) ? (
        <ErrorMessage id={errorId}>{`${questionText} ${ErrorMessages.InputRequired}`}</ErrorMessage>
      ) : null}
    </>
  );
};
